import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../lib/confirm";
import { Ban, Search, Lock, Unlock, Landmark, Home, CalendarClock, BadgeEuro, ShieldCheck } from "lucide-react";
import SuperAdminCategoryTabs from "../components/SuperAdminCategoryTabs";

/**
 * SUPERADMIN tool: date-based subscription control ("paid until" dates).
 *
 *  - OBEDIENCE date  → when it passes, the obedience's WEB panel locks for
 *    all its admins (incl. child-lodge admins).
 *  - CHILD-LODGE date → when it passes, that lodge's WEB panel locks for its
 *    own admins. Members are NOT affected.
 *  - MEMBER date     → when it passes, that brother's MOBILE app locks
 *    (every /m/* call + login refused) until the SuperAdmin extends the date.
 *  - Instant boolean suspend/unsuspend is kept for individual non-payers.
 */

function ExpiryBadge({ value, expired, t }) {
  if (!value) {
    return <span style={{ opacity: 0.6, fontSize: "0.8rem" }}>∞ {t("suspensions.unlimited", "Nelimitat")}</span>;
  }
  const d = String(value).slice(0, 10);
  return expired ? (
    <span style={{ color: "#a02020", fontWeight: 700, fontSize: "0.82rem" }}>
      ⛔ {t("suspensions.expired", "EXPIRAT")} · {d}
    </span>
  ) : (
    <span style={{ color: "#2c5e21", fontSize: "0.82rem" }}>
      ✓ {t("suspensions.paidUntil", "Plătit până la")} {d}
    </span>
  );
}

function SubscriptionCard({ icon: IconCmp, title, note, value, expired, dateVal, onDateChange, onSet, onUnlimited, busy, testPrefix, t }) {
  return (
    <div
      style={{ flex: "1 1 340px", maxWidth: 560, border: "1px solid #d8cfc0", borderRadius: 10, padding: "1rem 1.1rem", background: "#fbf9f4" }}
      data-testid={`${testPrefix}-card`}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, marginBottom: 4 }}>
        <IconCmp size={17} /> {title}
      </div>
      <div style={{ fontSize: "0.78rem", opacity: 0.7, marginBottom: 8 }}>{note}</div>
      <div style={{ marginBottom: 10 }} data-testid={`${testPrefix}-status`}>
        <ExpiryBadge value={value} expired={expired} t={t} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="date"
          className="input-lodge"
          value={dateVal}
          onChange={(e) => onDateChange(e.target.value)}
          data-testid={`${testPrefix}-date`}
        />
        <button className="btn-primary-gold" disabled={busy || !dateVal} onClick={onSet} data-testid={`${testPrefix}-set`}>
          {t("suspensions.setDate", "Setează data")}
        </button>
        <button className="btn-outline-gold" disabled={busy} onClick={onUnlimited} data-testid={`${testPrefix}-unlimited`}>
          {t("suspensions.setUnlimited", "Nelimitat")}
        </button>
      </div>
    </div>
  );
}

export default function SuspensionsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const [q, setQ] = useState("");
  const [lodgeFilter, setLodgeFilter] = useState("");
  const [suspendedOnly, setSuspendedOnly] = useState(false);
  const [members, setMembers] = useState([]);
  const [childLodges, setChildLodges] = useState([]);
  const [obedience, setObedience] = useState(null);
  const [suspendedCount, setSuspendedCount] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Date inputs for the three scopes
  const [obedDate, setObedDate] = useState("");
  const [lodgeDate, setLodgeDate] = useState("");
  const [memberDate, setMemberDate] = useState("");

  // Obedience (tenant) selector
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/lodges");
        setTenants(data || []);
        if ((data || []).length && !tenantId) setTenantId(data[0].id);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (!tenantId) return;
    try {
      const params = new URLSearchParams({ lodge_id: tenantId });
      if (q.trim()) params.set("q", q.trim());
      if (lodgeFilter) params.set("child_lodge_id", lodgeFilter);
      if (suspendedOnly) params.set("suspended_only", "true");
      const { data } = await api.get(`/superadmin/suspensions/members?${params.toString()}`);
      setMembers(data.members || []);
      setChildLodges(data.child_lodges || []);
      setObedience(data.obedience || null);
      setSuspendedCount(data.suspended_count || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    setSelected(new Set());
    setLodgeFilter("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    const tm = setTimeout(load, 250); // debounce the search bar
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, q, lodgeFilter, suspendedOnly]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = members.length > 0 && members.every((m) => selected.has(m.id));
  const toggleAll = () => {
    setSelected(() => (allVisibleSelected ? new Set() : new Set(members.map((m) => m.id))));
  };

  const apply = async (payload, confirmMsg, opts = {}) => {
    const ok = await confirm({
      title:
        opts.title ||
        (payload.action === "suspend"
          ? t("suspensions.confirmSuspendTitle", "Suspendare acces")
          : t("suspensions.confirmUnsuspendTitle", "Reactivare acces")),
      message: confirmMsg,
      confirmText:
        opts.confirmText ||
        (payload.action === "suspend"
          ? t("suspensions.confirmYes", "DA, SUSPENDĂ")
          : t("suspensions.reactivate", "REACTIVEAZĂ")),
      danger: opts.danger ?? payload.action === "suspend",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/suspensions/apply", {
        ...payload,
        lodge_id: tenantId,
        reason: reason.trim() || undefined,
      });
      toast.success(t("suspensions.appliedToast", "Acțiune aplicată — {{count}} membri afectați.", { count: data.affected }));
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleExempt = async ({ childLodgeId, name, current }) => {
    const next = !current;
    const ok = await confirm({
      title: t("suspensions.exemptTitle", "Exonerare plată abonament"),
      message: next
        ? t("suspensions.confirmExempt", "Exonerezi „{{name}}” de la plata abonamentului platformei? Nu va mai fi facturată și nu va fi blocată pentru neplată.", { name })
        : t("suspensions.confirmUnexempt", "Ridici exonerarea pentru „{{name}}”? Va fi inclusă din nou în facturare.", { name }),
      confirmText: next
        ? t("suspensions.exemptBtn", "EXONEREAZĂ")
        : t("suspensions.unexemptBtn", "RIDICĂ EXONERAREA"),
      danger: false,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.post("/superadmin/subscription-exempt", {
        lodge_id: tenantId,
        child_lodge_id: childLodgeId || undefined,
        exempt: next,
        reason: reason.trim() || undefined,
      });
      toast.success(t("suspensions.exemptToast", "Exonerare actualizată."));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const selectedLodge = useMemo(
    () => childLodges.find((l) => l.id === lodgeFilter) || null,
    [childLodges, lodgeFilter]
  );
  const tenantName = useMemo(
    () => tenants.find((x) => x.id === tenantId)?.name || "",
    [tenants, tenantId]
  );

  return (
    <div data-testid="suspensions-page">
      <SuperAdminCategoryTabs active="suspensions" />

      <h1 className="page-title" style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
        <Ban size={26} /> {t("suspensions.title", "Abonamente & Suspendări")}
      </h1>
      <p className="page-subtitle">
        {t("suspensions.subtitle", "Setează data până la care s-a plătit, pentru o obediență, o lojă sau frați individuali. La expirarea datei: obediența/loja pierde accesul la panoul WEB, iar fratele pierde accesul la aplicația MOBILĂ — până la reînnoire.")}
      </p>

      {/* Obedience selector */}
      <div className="flex flex-wrap items-center gap-3" style={{ margin: "1rem 0" }}>
        <label style={{ fontWeight: 700, fontSize: "0.85rem" }}>{t("suspensions.obedience", "Obediență")}:</label>
        <select
          className="input-lodge"
          style={{ minWidth: 320 }}
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          data-testid="suspensions-tenant-select"
        >
          {tenants.map((x) => (
            <option key={x.id} value={x.id}>{x.name}</option>
          ))}
        </select>
        <select
          className="input-lodge"
          value={lodgeFilter}
          onChange={(e) => setLodgeFilter(e.target.value)}
          data-testid="suspensions-lodge-filter"
        >
          <option value="">{t("suspensions.allLodges", "Toată obediența")}</option>
          {childLodges.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
      </div>

      {/* Subscription cards: obedience (web) + selected child lodge (web) */}
      <div className="flex flex-wrap gap-3" style={{ margin: "1rem 0 1.4rem" }}>
        <SubscriptionCard
          icon={Landmark}
          title={t("suspensions.obedienceCard", "Abonament obediență — „{{name}}”", { name: tenantName })}
          note={t("suspensions.obedienceCardNote", "La expirare se blochează panoul WEB pentru toți adminii obedienței și ai lojilor ei. Membrii NU sunt afectați pe mobil.")}
          value={obedience?.access_expires_at}
          expired={obedience?.expired}
          dateVal={obedDate}
          onDateChange={setObedDate}
          busy={busy}
          testPrefix="subscription-obedience"
          t={t}
          onSet={() =>
            apply(
              { action: "set_expiry", whole_obedience: true, expires_at: obedDate },
              t("suspensions.confirmObedDate", "Setezi abonamentul obedienței „{{name}}” plătit până la {{date}}? La expirare, panoul web se blochează pentru toți adminii ei.", { name: tenantName, date: obedDate }),
              { title: t("suspensions.expiryTitle", "Dată abonament"), confirmText: t("suspensions.setDate", "Setează data"), danger: false }
            )
          }
          onUnlimited={() =>
            apply(
              { action: "set_expiry", whole_obedience: true, expires_at: null },
              t("suspensions.confirmObedUnlimited", "Setezi acces NELIMITAT (fără dată de expirare) pentru obediența „{{name}}”?", { name: tenantName }),
              { title: t("suspensions.expiryTitle", "Dată abonament"), confirmText: t("suspensions.setUnlimited", "Nelimitat"), danger: false }
            )
          }
        />
        {selectedLodge && (
          <SubscriptionCard
            icon={Home}
            title={t("suspensions.lodgeCard", "Abonament lojă — „{{name}}”", { name: selectedLodge.name })}
            note={t("suspensions.lodgeCardNote", "La expirare se blochează panoul WEB pentru adminii acestei loji. Membrii NU sunt afectați pe mobil.")}
            value={selectedLodge.subscription_expires_at}
            expired={selectedLodge.subscription_expired}
            dateVal={lodgeDate}
            onDateChange={setLodgeDate}
            busy={busy}
            testPrefix="subscription-lodge"
            t={t}
            onSet={() =>
              apply(
                { action: "set_expiry", child_lodge_id: selectedLodge.id, expires_at: lodgeDate },
                t("suspensions.confirmLodgeDate", "Setezi abonamentul lojii „{{name}}” plătit până la {{date}}?", { name: selectedLodge.name, date: lodgeDate }),
                { title: t("suspensions.expiryTitle", "Dată abonament"), confirmText: t("suspensions.setDate", "Setează data"), danger: false }
              )
            }
            onUnlimited={() =>
              apply(
                { action: "set_expiry", child_lodge_id: selectedLodge.id, expires_at: null },
                t("suspensions.confirmLodgeUnlimited", "Setezi acces NELIMITAT pentru loja „{{name}}”?", { name: selectedLodge.name }),
                { title: t("suspensions.expiryTitle", "Dată abonament"), confirmText: t("suspensions.setUnlimited", "Nelimitat"), danger: false }
              )
            }
          />
        )}
      </div>

      {/* SaaS billing exemption (Abonament) */}
      <div
        style={{ border: "1px solid #d8cfc0", borderRadius: 10, padding: "1rem 1.1rem", background: "#fbf9f4", margin: "0 0 1.4rem" }}
        data-testid="sub-exempt-section"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, marginBottom: 4 }}>
          <BadgeEuro size={17} /> {t("suspensions.exemptTitle", "Exonerare plată abonament")}
        </div>
        <div style={{ fontSize: "0.78rem", opacity: 0.7, marginBottom: 12 }}>
          {t("suspensions.exemptNote", "Obediențele/lojile exonerate NU plătesc abonamentul platformei (Abonament SaaS) și NU se blochează pentru neplată. Lojile exonerate și membrii lor mobili nu se numără în calculul prețului.")}
        </div>
        {/* Whole obedience */}
        <div
          className="flex flex-wrap items-center gap-3"
          style={{ padding: "0.55rem 0.7rem", borderRadius: 8, background: obedience?.sub_exempt ? "#eaf4e8" : "rgba(0,0,0,0.03)", marginBottom: 8 }}
          data-testid="exempt-obedience-row"
        >
          <Landmark size={15} />
          <strong style={{ fontSize: "0.88rem" }}>{tenantName}</strong>
          <span style={{ fontSize: "0.72rem", opacity: 0.6 }}>({t("suspensions.wholeObedience", "toată obediența")})</span>
          {obedience?.sub_exempt ? (
            <span style={{ color: "#2c5e21", fontWeight: 700, fontSize: "0.78rem", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid="exempt-obedience-badge">
              <ShieldCheck size={13} /> {t("suspensions.exemptBadge", "EXONERATĂ — GRATUIT")}
            </span>
          ) : (
            <span style={{ opacity: 0.6, fontSize: "0.78rem" }}>{t("suspensions.billedBadge", "se facturează")}</span>
          )}
          <button
            className={obedience?.sub_exempt ? "btn-outline-gold" : "btn-primary-gold"}
            style={{ marginLeft: "auto" }}
            disabled={busy || !obedience}
            onClick={() => toggleExempt({ childLodgeId: null, name: tenantName, current: !!obedience?.sub_exempt })}
            data-testid="exempt-obedience-toggle"
          >
            {obedience?.sub_exempt
              ? t("suspensions.unexemptBtn", "RIDICĂ EXONERAREA")
              : t("suspensions.exemptBtn", "EXONEREAZĂ")}
          </button>
        </div>
        {/* Per child lodge */}
        {childLodges.map((l) => (
          <div
            key={l.id}
            className="flex flex-wrap items-center gap-3"
            style={{ padding: "0.45rem 0.7rem", borderRadius: 8, background: l.sub_exempt ? "#eaf4e8" : "transparent", borderTop: "1px solid rgba(0,0,0,0.05)" }}
            data-testid={`exempt-lodge-row-${l.id}`}
          >
            <Home size={14} style={{ opacity: 0.7 }} />
            <span style={{ fontSize: "0.85rem" }}>{l.name}</span>
            {obedience?.sub_exempt ? (
              <span style={{ color: "#2c5e21", fontSize: "0.75rem", fontStyle: "italic" }}>
                {t("suspensions.exemptViaObedience", "exonerată prin obediență")}
              </span>
            ) : l.sub_exempt ? (
              <span style={{ color: "#2c5e21", fontWeight: 700, fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`exempt-lodge-badge-${l.id}`}>
                <ShieldCheck size={12} /> {t("suspensions.exemptBadgeLodge", "EXONERATĂ")}
              </span>
            ) : (
              <span style={{ opacity: 0.55, fontSize: "0.75rem" }}>{t("suspensions.billedBadge", "se facturează")}</span>
            )}
            {!obedience?.sub_exempt && (
              <button
                className={l.sub_exempt ? "btn-outline-gold" : "btn-primary-gold"}
                style={{ marginLeft: "auto", fontSize: "0.7rem", padding: "0.3rem 0.7rem" }}
                disabled={busy}
                onClick={() => toggleExempt({ childLodgeId: l.id, name: l.name, current: !!l.sub_exempt })}
                data-testid={`exempt-lodge-toggle-${l.id}`}
              >
                {l.sub_exempt
                  ? t("suspensions.unexemptBtn", "RIDICĂ EXONERAREA")
                  : t("suspensions.exemptBtn", "EXONEREAZĂ")}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Toolbar: search + suspended-only */}
      <div className="flex flex-wrap items-center gap-3" style={{ margin: "1rem 0" }}>
        <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 380 }}>
          <Search size={15} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", opacity: 0.5 }} />
          <input
            className="input-lodge"
            style={{ paddingLeft: 32, width: "100%" }}
            placeholder={t("suspensions.searchPh", "Caută membru după nume...")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="suspensions-search"
          />
        </div>
        <label className="flex items-center gap-2" style={{ fontSize: "0.85rem", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={suspendedOnly}
            onChange={(e) => setSuspendedOnly(e.target.checked)}
            data-testid="suspensions-only-toggle"
          />
          {t("suspensions.onlySuspended", "Doar blocați/expirați")} ({suspendedCount})
        </label>
      </div>

      {/* Member-level "paid until" date for the current selection */}
      <div
        className="flex flex-wrap items-center gap-3"
        style={{ marginBottom: "1rem", border: "1px solid #d8cfc0", borderRadius: 10, padding: "0.8rem 1rem", background: "#fbf9f4" }}
        data-testid="member-expiry-bar"
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: "0.85rem" }}>
          <CalendarClock size={16} /> {t("suspensions.memberExpiry", "Frați selectați — plătit până la (mobil)")}:
        </span>
        <input
          type="date"
          className="input-lodge"
          value={memberDate}
          onChange={(e) => setMemberDate(e.target.value)}
          data-testid="member-expiry-date"
        />
        <button
          className="btn-primary-gold"
          disabled={busy || selected.size === 0 || !memberDate}
          onClick={() =>
            apply(
              { action: "set_expiry", member_ids: [...selected], expires_at: memberDate },
              t("suspensions.confirmMemberDate", "Setezi „plătit până la {{date}}” pentru {{count}} frați selectați? La expirarea datei, aplicația mobilă se blochează.", { date: memberDate, count: selected.size }),
              { title: t("suspensions.expiryTitle", "Dată abonament"), confirmText: t("suspensions.setDate", "Setează data"), danger: false }
            )
          }
          data-testid="set-expiry-selected-btn"
        >
          {t("suspensions.setDateSelected", "Setează data ({{count}})", { count: selected.size })}
        </button>
        <button
          className="btn-outline-gold"
          disabled={busy || selected.size === 0}
          onClick={() =>
            apply(
              { action: "set_expiry", member_ids: [...selected], expires_at: null },
              t("suspensions.confirmMemberUnlimited", "Setezi acces NELIMITAT pe mobil pentru {{count}} frați selectați?", { count: selected.size }),
              { title: t("suspensions.expiryTitle", "Dată abonament"), confirmText: t("suspensions.setUnlimited", "Nelimitat"), danger: false }
            )
          }
          data-testid="clear-expiry-selected-btn"
        >
          {t("suspensions.setUnlimited", "Nelimitat")}
        </button>
      </div>

      {/* Reason + instant suspend/unsuspend */}
      <div className="flex flex-wrap items-center gap-3" style={{ marginBottom: "1.4rem" }}>
        <input
          className="input-lodge"
          style={{ flex: "1 1 280px", maxWidth: 420 }}
          placeholder={t("suspensions.reasonPh", "Motiv (ex.: Neplată cotizație — factura nr. ...)")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="suspensions-reason"
        />
        <button
          className="btn-danger flex items-center gap-2"
          disabled={busy || selected.size === 0}
          onClick={() => apply(
            { action: "suspend", member_ids: [...selected] },
            t("suspensions.confirmSelected", "Suspenzi IMEDIAT accesul la aplicație pentru {{count}} membri selectați?", { count: selected.size })
          )}
          data-testid="suspend-selected-btn"
        >
          <Lock size={15} /> {t("suspensions.suspendSelected", "Suspendă imediat")} ({selected.size})
        </button>
        <button
          className="btn-outline-gold flex items-center gap-2"
          disabled={busy || selected.size === 0}
          onClick={() => apply(
            { action: "unsuspend", member_ids: [...selected] },
            t("suspensions.confirmUnselected", "Reactivezi accesul pentru {{count}} membri selectați? (se ridică și data expirată)", { count: selected.size })
          )}
          data-testid="unsuspend-selected-btn"
        >
          <Unlock size={15} /> {t("suspensions.unsuspendSelected", "Reactivează selecția")}
        </button>
      </div>

      {/* Members table */}
      <table className="lodge-table" data-testid="suspensions-table">
        <thead>
          <tr>
            <th style={{ width: 36 }}>
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} data-testid="suspensions-select-all" />
            </th>
            <th>{t("suspensions.colName", "Nume")}</th>
            <th>{t("suspensions.colLodge", "Lojă")}</th>
            <th>{t("suspensions.colExpiry", "Plătit până la (mobil)")}</th>
            <th>{t("suspensions.colStatus", "Status aplicație")}</th>
            <th style={{ width: 160 }}></th>
          </tr>
        </thead>
        <tbody>
          {members.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: "center", opacity: 0.6, padding: "1.4rem" }}>{t("suspensions.empty", "Niciun membru găsit.")}</td></tr>
          )}
          {members.map((m) => (
            <tr
              key={m.id}
              data-testid={`suspension-row-${m.id}`}
              style={m.app_suspended || m.subscription_expired ? { background: "#fdecec" } : undefined}
            >
              <td>
                <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} data-testid={`suspension-check-${m.id}`} />
              </td>
              <td>
                <strong>{m.name}</strong>
                {m.grade ? <span style={{ opacity: 0.6, marginLeft: 8, fontSize: "0.8rem" }}>{m.grade}</span> : null}
              </td>
              <td>{m.child_lodge_name || t("suspensions.hq", "Obediență (sediu)")}</td>
              <td data-testid={`member-expiry-${m.id}`}>
                <ExpiryBadge value={m.subscription_expires_at} expired={m.subscription_expired} t={t} />
              </td>
              <td>
                {m.app_suspended ? (
                  <span style={{ color: "#a02020", fontWeight: 700, fontSize: "0.82rem" }} data-testid={`suspended-badge-${m.id}`}>
                    ⛔ {t("suspensions.suspended", "SUSPENDAT")}
                    {m.app_suspended_at ? ` · ${String(m.app_suspended_at).slice(0, 10)}` : ""}
                    {m.app_suspended_reason ? <div style={{ fontWeight: 400, opacity: 0.8 }}>{m.app_suspended_reason}</div> : null}
                  </span>
                ) : m.subscription_expired ? (
                  <span style={{ color: "#a02020", fontWeight: 700, fontSize: "0.82rem" }} data-testid={`expired-badge-${m.id}`}>
                    ⛔ {t("suspensions.expired", "EXPIRAT")}
                  </span>
                ) : (
                  <span style={{ color: "#2c5e21", fontSize: "0.82rem" }}>✓ {t("suspensions.active", "Activ")}</span>
                )}
              </td>
              <td style={{ textAlign: "right" }}>
                {m.app_suspended || m.subscription_expired ? (
                  <button
                    className="btn-outline-gold"
                    disabled={busy}
                    onClick={() => apply(
                      { action: "unsuspend", member_ids: [m.id] },
                      t("suspensions.confirmOneUn", "Reactivezi accesul pentru {{name}}?", { name: m.name })
                    )}
                    data-testid={`unsuspend-one-${m.id}`}
                  >
                    {t("suspensions.reactivate", "REACTIVEAZĂ")}
                  </button>
                ) : (
                  <button
                    className="btn-danger"
                    disabled={busy}
                    onClick={() => apply(
                      { action: "suspend", member_ids: [m.id] },
                      t("suspensions.confirmOne", "Suspenzi accesul la aplicație pentru {{name}}?", { name: m.name })
                    )}
                    data-testid={`suspend-one-${m.id}`}
                  >
                    {t("suspensions.suspend", "SUSPENDĂ")}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
